import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useFlashcards } from '../context/FlashcardContext';
import { useAuth } from '../context/AuthContext';
import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { initializeApp, getApp } from 'firebase/app';
import { firebaseConfig } from '../../firebase.config';
import { updateProfile } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Person,
  Style,
  LibraryBooks,
  Class,
  Settings,
  Edit,
  Check,
  Close,
  AutoAwesome,
  Restore,
  AttachMoney,
  Logout,
  School,
  ArrowBack,
} from '@mui/icons-material';
import iapService from '../services/iapService';
import stripeService from '../services/stripeService';

// Initialize Firebase if needed
let app;
try {
  app = getApp();
} catch (error) {
  app = initializeApp(firebaseConfig);
}
const db = getFirestore(app);
const auth = getAuth(app);

const ProfileScreen = () => {
  const navigation = useNavigation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { user, getRemainingAIFlashcards, isPro, setUser, syncUserDataFromFirestore } = useFlashcards();
  const { user: authUser, signOut } = useAuth();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(user?.name || '');
  const [restoringPurchases, setRestoringPurchases] = useState(false);
  const [stripeInitialized, setStripeInitialized] = useState(false);

  // Initialize Stripe service on mount
  useEffect(() => {
    const initStripe = async () => {
      try {
        const initialized = await stripeService.initialize();
        setStripeInitialized(initialized);
      } catch (error) {
        console.error('Error initializing Stripe:', error);
      }
    };
    initStripe();
  }, []);

  const formatStudyTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName.trim() === user?.name) {
      setIsEditingName(false);
      return;
    }

    try {
      const newName = editedName.trim();
      
      // Update Firebase Auth display name
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: newName,
        });
      }

      // Update Firestore
      if (auth.currentUser) {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userDocRef, {
          name: newName,
        });
      }

      // Update local context
      setUser({ ...user, name: newName });

      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
      // Still close the edit mode even if there's an error
      setIsEditingName(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(user?.name || '');
    setIsEditingName(false);
  };

  const handleRestorePurchases = async () => {
    setRestoringPurchases(true);
    try {
      await iapService.restorePurchases();
      // Show success message
    } catch (error) {
      console.error('Error restoring purchases:', error);
    } finally {
      setRestoringPurchases(false);
    }
  };

  const remainingAI = getRemainingAIFlashcards();
  const earned = user?.ai_flashcards_earned || 0;
  const used = user?.ai_flashcards_used || 0;
  
  // Check if in demo mode
  const isDemoMode = !authUser && typeof window !== 'undefined' && localStorage.getItem('isDemoMode') === 'true';
  const totalAI = isDemoMode ? 20 : earned;
  
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
          </TouchableOpacity>
          <Text style={[styles.title, { color: theme.text }]}>{t('profile.title')}</Text>
          <View style={{ width: 40 }} /> {/* Spacer for centering */}
        </View>

        {/* Profile Info Card */}
        <View style={[styles.card, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <View style={styles.profileHeader}>
            <View style={[styles.avatar, { backgroundColor: theme.primary }]}>
              <Person sx={{ fontSize: 32, color: 'white' }} />
            </View>
            <View style={styles.profileInfo}>
              {isEditingName ? (
                <View style={styles.editNameContainer}>
                  <TextInput
                    style={[styles.nameInput, { color: theme.inputText, borderColor: theme.inputBorder, backgroundColor: theme.inputBackground }]}
                    value={editedName}
                    onChangeText={setEditedName}
                    autoFocus
                  />
                  <View style={styles.editButtons}>
                    <TouchableOpacity
                      style={[styles.editButton, { backgroundColor: theme.success }]}
                      onPress={handleSaveName}
                    >
                      <Check sx={{ fontSize: 18, color: 'white' }} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.editButton, { backgroundColor: theme.error }]}
                      onPress={handleCancelEdit}
                    >
                      <Close sx={{ fontSize: 18, color: 'white' }} />
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <>
                  <Text style={[styles.userName, { color: theme.text }]}>
                    {user?.name || 'User'}
                  </Text>
                  <TouchableOpacity
                    style={styles.editNameButton}
                    onPress={() => setIsEditingName(true)}
                  >
                    <Edit sx={{ fontSize: 16, color: theme.primary }} />
                    <Text style={[styles.editNameText, { color: theme.primary }]}>
                      {t('profile.editName')}
                    </Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </View>

        {/* Lifetime Achievements */}
        <View style={[styles.card, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>
            {t('profile.lifetimeAchievements')}
          </Text>
          <View style={[styles.statsGrid, { marginTop: 24 }]}>
            <View style={[styles.statItem, { backgroundColor: theme.card }]}>
              <View style={[styles.statIcon, { backgroundColor: theme.primaryLight }]}>
                <Style sx={{ fontSize: 24, color: theme.success }} />
              </View>
              <Text style={[styles.statValue, { color: theme.text }]}>
                {user?.lifelongCardsCreated || 0}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                {t('home.stats.cards')}
              </Text>
            </View>
            <View style={[styles.statItem, { backgroundColor: theme.card }]}>
              <View style={[styles.statIcon, { backgroundColor: theme.primaryLight }]}>
                <LibraryBooks sx={{ fontSize: 24, color: theme.accent }} />
              </View>
              <Text style={[styles.statValue, { color: theme.text }]}>
                {user?.lifelongSetsCreated || 0}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                {t('home.stats.sets')}
              </Text>
            </View>
            <View style={[styles.statItem, { backgroundColor: theme.card }]}>
              <View style={[styles.statIcon, { backgroundColor: theme.primaryLight }]}>
                <Class sx={{ fontSize: 24, color: theme.primary }} />
              </View>
              <Text style={[styles.statValue, { color: theme.text }]}>
                {user?.lifelongClassesCreated || 0}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>
                {t('home.stats.classes')}
              </Text>
            </View>
          </View>
        </View>

        {/* AI Flashcards */}
        <View style={[styles.card, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <View style={styles.aiFlashcardsHeader}>
            <View style={[styles.aiIcon, { backgroundColor: theme.primaryLight }]}>
              <AutoAwesome sx={{ fontSize: 24, color: theme.primary }} />
            </View>
            <View style={styles.aiFlashcardsInfo}>
              <Text style={[styles.aiFlashcardsTitle, { color: theme.text }]}>
                {t('profile.aiFlashcardsRemaining')}
              </Text>
            </View>
            <View style={styles.aiFlashcardsCount}>
              <Text style={[styles.aiFlashcardsNumber, { color: theme.primary }]}>
                {isPro() ? '∞' : `${remainingAI}/${totalAI}`}
              </Text>
            </View>
          </View>
          {!isPro() && remainingAI === 0 && (
            <TouchableOpacity
              style={[styles.upgradeButton, { backgroundColor: theme.primary }]}
              onPress={() => navigation.navigate('Pricing')}
            >
              <Text style={[styles.upgradeButtonText, { color: theme.buttonText }]}>
                {t('noAIFlashcards.upgradeToPro')}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Subscription Status */}
        <View style={[styles.card, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          {isPro() ? (
            <View style={styles.subscriptionContent}>
              <View style={styles.subscriptionHeader}>
                <View style={[styles.proBadge, { backgroundColor: theme.success || '#50E3C2' }]}>
                  <Text style={[styles.proText, { color: 'white' }]}>PRO</Text>
                </View>
              </View>
              <Text style={[styles.proMessage, { color: theme.text, marginTop: 12, marginBottom: 20 }]}>
                You're on the Pro plan! Enjoy unlimited access to all features.
              </Text>
              <TouchableOpacity
                style={[styles.cancelButton, { backgroundColor: 'transparent', borderColor: theme.error }]}
                onPress={async () => {
                  try {
                    // Ensure Stripe is initialized
                    if (!stripeInitialized) {
                      const initialized = await stripeService.initialize();
                      if (!initialized) {
                        throw new Error('Failed to initialize Stripe service');
                      }
                      setStripeInitialized(true);
                    }
                    await stripeService.redirectToBillingPortal();
                  } catch (error) {
                    console.error('❌ Error opening billing portal:', error);
                    alert(`Failed to open billing portal: ${error.message || 'Unknown error'}\n\nPlease try again or contact support.`);
                  }
                }}
              >
                <Close sx={{ fontSize: 18, color: theme.error }} />
                <Text style={[styles.cancelButtonText, { color: theme.error }]}>
                  Cancel Subscription
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.subscriptionContent}>
              <Text style={[styles.proMessage, { color: theme.text, marginBottom: 8, fontWeight: '600' }]}>
                Free Plan
              </Text>
              <Text style={[styles.proMessage, { color: theme.textSecondary, fontSize: 14 }]}>
                Upgrade to Pro for unlimited sets and AI flashcards
              </Text>
            </View>
          )}
        </View>

        {/* Actions */}
        <View style={styles.actionsContainer}>
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            onPress={() => navigation.navigate('Settings')}
          >
            <Settings sx={{ fontSize: 20, color: theme.primary }} />
            <Text style={[styles.actionButtonText, { color: theme.text }]}>
              {t('profile.settings')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            onPress={handleRestorePurchases}
            disabled={restoringPurchases}
          >
            <Restore sx={{ fontSize: 20, color: theme.primary }} />
            <Text style={[styles.actionButtonText, { color: theme.text }]}>
              {restoringPurchases ? t('profile.restoringPurchases') : t('profile.restorePurchases')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            onPress={() => navigation.navigate('Pricing')}
          >
            <AttachMoney sx={{ fontSize: 20, color: theme.primary }} />
            <Text style={[styles.actionButtonText, { color: theme.text }]}>
              {t('profile.pricing')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            onPress={() => navigation.navigate('/onboarding')}
          >
            <School sx={{ fontSize: 20, color: theme.primary }} />
            <Text style={[styles.actionButtonText, { color: theme.text }]}>
              {t('profile.viewOnboarding')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
            onPress={async () => {
              try {
                await signOut();
                navigation.navigate('Login');
              } catch (error) {
                console.error('Error signing out:', error);
              }
            }}
          >
            <Logout sx={{ fontSize: 20, color: theme.error }} />
            <Text style={[styles.actionButtonText, { color: theme.error }]}>
              {t('profile.logout')}
            </Text>
          </TouchableOpacity>
    </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    flex: 1,
    textAlign: 'center',
  },
  card: {
    marginHorizontal: 24,
    marginBottom: 16,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  profileHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  profileInfo: {
    flex: 1,
  },
  userName: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
  },
  editNameButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
    cursor: 'pointer',
  },
  editNameText: {
    fontSize: 14,
    fontWeight: '500',
  },
  editNameContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  nameInput: {
    flex: 1,
    fontSize: 24,
    fontWeight: '700',
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
  },
  editButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 20,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    textAlign: 'center',
  },
  aiFlashcardsHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  aiIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  aiFlashcardsInfo: {
    flex: 1,
  },
  aiFlashcardsTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  aiFlashcardsSubtitle: {
    fontSize: 14,
  },
  aiFlashcardsCount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  aiFlashcardsNumber: {
    fontSize: 32,
    fontWeight: '700',
  },
  upgradeButton: {
    marginTop: 16,
    padding: 14,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  upgradeButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  subscriptionContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  subscriptionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  proBadge: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  proText: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 1,
  },
  proMessage: {
    fontSize: 16,
    lineHeight: 1.6,
  },
  cancelButton: {
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1.5,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  actionsContainer: {
    marginHorizontal: 24,
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  actionButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ProfileScreen;
